import { useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Stack, Typography, Card, Alert, Paper, Chip, Button,
  Divider, CircularProgress, Link,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';

import { useAuth } from '../../context/AuthContext';
import {
  useCuentaCorrienteResumen,
  useCuentaCorrienteDetalle,
} from '../../hooks/useCuentaCorriente';
import { formatCurrency, formatCuit, formatDate } from '../../utils/format';
import {
  type CuentaCorrienteDetalle,
  getEstadoCtaCteLabel,
  getEstadoCtaCteColor,
} from '../../types/cuentaCorriente';

interface Props {
  /** Si viene por prop, se usa (caso /mi-cuenta-corriente). Si no, lee de :empresaId */
  empresaIdProp?: string;
  /** Hide "Volver a cuenta corriente" (caso /mi-cuenta-corriente) */
  hideVolver?: boolean;
}

export default function CuentaCorrienteDetalle({
  empresaIdProp,
  hideVolver,
}: Props = {}) {
  const params = useParams<{ empresaId: string }>();
  const navigate = useNavigate();
  const { effectiveRole, effectiveEmpresaId } = useAuth();

  const empresaId = empresaIdProp ?? params.empresaId ?? effectiveEmpresaId ?? undefined;

  const { data: detalle = [], isLoading, isError } = useCuentaCorrienteDetalle(empresaId);
  // Para mostrar el header con totales: leemos del resumen (no pega query extra
  // a la BD porque está cacheado, y para empresa solo trae 1 fila).
  const { data: resumen = [] } = useCuentaCorrienteResumen();
  const totales = useMemo(
    () => resumen.find((r) => r.empresa_id === empresaId),
    [resumen, empresaId],
  );

  if (!empresaId) {
    return (
      <Alert severity="error">
        No se pudo identificar la empresa. Volvé al{' '}
        <Link component={RouterLink} to="/">inicio</Link>.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Error al cargar el detalle de cuenta corriente.</Alert>;
  }

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columns: GridColDef<CuentaCorrienteDetalle>[] = [
    {
      field: 'periodo',
      headerName: 'Período',
      width: 130,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Stack spacing={0}>
          <Typography variant="body2" fontWeight={500}>
            {p.row.periodo}
          </Typography>
          {p.row.periodo_excepcional && (
            <Typography variant="caption" color="text.secondary">
              excepcional
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'cupon_codigo',
      headerName: 'Cupón',
      width: 220,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ReceiptLongIcon fontSize="small" color="action" />
          <Typography variant="caption" fontFamily="monospace">
            {p.row.cupon_codigo}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'monto_emitido',
      headerName: 'Emitido',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2">
            {formatCurrency(p.row.monto_emitido)}
          </Typography>
          {Number(p.row.interes) > 0 && (
            <Typography variant="caption" color="error.main">
              + {formatCurrency(p.row.interes)} mora
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'monto_pagado',
      headerName: 'Pagado',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Typography variant="body2" color="success.main">
          {formatCurrency(p.row.monto_pagado)}
        </Typography>
      ),
    },
    {
      field: 'saldo',
      headerName: 'Saldo',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={Number(p.row.saldo) > 0 ? 'error.main' : 'text.primary'}
        >
          {formatCurrency(p.row.saldo)}
        </Typography>
      ),
    },
    {
      field: 'fecha_vencimiento',
      headerName: 'Vencimiento',
      width: 120,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Typography
          variant="caption"
          color={p.row.esta_vencido ? 'error.main' : 'text.primary'}
        >
          {formatDate(p.row.fecha_vencimiento)}
        </Typography>
      ),
    },
    {
      field: 'estado_cta_cte',
      headerName: 'Estado',
      width: 130,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Chip
          label={getEstadoCtaCteLabel(p.row.estado_cta_cte)}
          color={getEstadoCtaCteColor(p.row.estado_cta_cte)}
          size="small"
        />
      ),
    },
    {
      field: 'acciones',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Button
          size="small"
          onClick={() => navigate(`/cupones/${p.row.cupon_id}`)}
        >
          Ver
        </Button>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  const empresaLabel = totales?.empresa_razon_social ?? detalle[0]?.empresa_razon_social ?? '—';
  const empresaCuit  = totales?.empresa_cuit         ?? detalle[0]?.empresa_cuit         ?? '';
  const puedeVolver  = !hideVolver && effectiveRole !== 'admin_empresa';

  return (
    <Box>
      {puedeVolver && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/cuenta-corriente')}
          sx={{ mb: 2 }}
        >
          Volver a cuenta corriente
        </Button>
      )}

      {/* Header con totales */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {empresaLabel}
              </Typography>
              {empresaCuit && (
                <Typography variant="caption" color="text.secondary">
                  CUIT {formatCuit(empresaCuit)}
                </Typography>
              )}
            </Box>
          </Stack>

          {totales && (
            <Stack
              direction="row"
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Stack alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  Emitido
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(totales.monto_emitido_total)}
                </Typography>
              </Stack>
              <Stack alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  Pagado
                </Typography>
                <Typography variant="body2" color="success.main">
                  {formatCurrency(totales.monto_pagado_total)}
                </Typography>
              </Stack>
              <Stack alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  Saldo
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={Number(totales.saldo_total) > 0 ? 'error.main' : 'success.main'}
                >
                  {formatCurrency(totales.saldo_total)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>

        {totales && totales.cant_vencidos > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Hay <strong>{totales.cant_vencidos} cupón(es) vencido(s)</strong>. El más
            antiguo vence el {formatDate(totales.vencimiento_mas_antiguo)}.
          </Alert>
        )}
      </Paper>

      <Card>
        <DataGrid
          rows={detalle}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          getRowId={(r) => r.cupon_id}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'periodo', sort: 'desc' }] },
          }}
          sx={{ border: 0 }}
          localeText={{
            noRowsLabel: 'Esta empresa no tiene cupones emitidos',
          }}
        />
      </Card>
    </Box>
  );
}
